// React
import { useCallback } from 'react'

// Models
import { useStores } from 'models'

// Interfaces
import {
  ICertificationWebpagePurposeOfAssessmentCompetencyUnitGetListAttrs,
  ICertificationWebpagePurposeOfAssessmentGetDetailAttrs,
  ICertificationWebpagePurposeOfAssessmentGetListAttrs,
  ICertificationWebpagePurposeOfAssessmentUpdateAttrs,
} from 'interfaces/webpage/certification/registration/purpose-of-assessment/purpose-of-assessment-view.interface'

const useCertificationPurposeOfAssessment = () => {
  // Hook
  const {
    certificationWebpagePurposeOfAssessment: {
      certificationWebpagePurposeOfAssessment_loading,
      certificationWebpagePurposeOfAssessment_list,
      certificationWebpagePurposeOfAssessment_competencyUnitList,
      certificationWebpagePurposeOfAssessment_detail,
      certificationWebpagePurposeOfAssessment_CLEAR_LIST,
      certificationWebpagePurposeOfAssessment_CLEAR_DETAIL,
      certificationWebpagePurposeOfAssessment_CLEAR_COMPETENCY_UNIT_LIST,
      certificationWebpagePurposeOfAssessment_fetchList: fetchList,
      certificationWebpagePurposeOfAssessment_fetchCompetencyUnitList: fetchCompetencyUnitList,
      certificationWebpagePurposeOfAssessment_fetchDetail: fetchDetail,
      certificationWebpagePurposeOfAssessment_update: update,
    },
  } = useStores()

  /**
   * @description Fetch purpose of assessment competency unit list
   *
   * @param {ICertificationWebpagePurposeOfAssessmentCompetencyUnitGetListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpagePurposeOfAssessment_fetchCompetencyUnitList = useCallback(
    (payload: ICertificationWebpagePurposeOfAssessmentCompetencyUnitGetListAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchCompetencyUnitList({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [fetchCompetencyUnitList],
  )

  /**
   * @description Fetch purpose of assessment list
   *
   * @param {ICertificationWebpagePurposeOfAssessmentGetListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpagePurposeOfAssessment_fetchList = useCallback(
    (payload: ICertificationWebpagePurposeOfAssessmentGetListAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchList({
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [fetchList],
  )

  /**
   * @description Get purpose of assessment detail
   *
   * @param {ICertificationWebpagePurposeOfAssessmentGetDetailAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpagePurposeOfAssessment_fetchDetail = useCallback(
    (payload: ICertificationWebpagePurposeOfAssessmentGetDetailAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchDetail({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [fetchDetail],
  )

  /**
   * @description Update purpose of assessment
   *
   * @param {ICertificationWebpagePurposeOfAssessmentUpdateAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpagePurposeOfAssessment_update = useCallback(
    (payload: ICertificationWebpagePurposeOfAssessmentUpdateAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        update({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [update],
  )

  return {
    certificationWebpagePurposeOfAssessment_loading,
    certificationWebpagePurposeOfAssessment_list,
    certificationWebpagePurposeOfAssessment_detail,
    certificationWebpagePurposeOfAssessment_competencyUnitList,
    certificationWebpagePurposeOfAssessment_CLEAR_LIST,
    certificationWebpagePurposeOfAssessment_CLEAR_DETAIL,
    certificationWebpagePurposeOfAssessment_CLEAR_COMPETENCY_UNIT_LIST,
    certificationWebpagePurposeOfAssessment_fetchList,
    certificationWebpagePurposeOfAssessment_fetchDetail,
    certificationWebpagePurposeOfAssessment_update,
    certificationWebpagePurposeOfAssessment_fetchCompetencyUnitList,
  }
}

export { useCertificationPurposeOfAssessment }
